import type {
  HabitaPendingDataItem,
  HabitaPendingFileItem,
  HabitaPendingItems,
  HabitaProcessValidationState,
  HabitaProcessStatusHistory,
  HabitaProcessStatusHistoryEntry,
  HabitaProcessStatusTransitionRequest,
  HabitaProcessStatusTransitionResponse,
  HabitaRequestDocumentAdjustmentInput,
  HabitaRequestDocumentAdjustmentResult,
  HabitaSubmitValidationRequest,
  HabitaSubmitValidationResponse,
  HabitaValidateFolderInput,
  HabitaValidateFolderResult,
  HabitaValidationCycle,
  HabitaValidationDataItem,
  HabitaValidationFileItem,
} from '../../shared/models';
import { HttpError, HttpRepository } from './HttpRepository';
import {
  getHabitaProcessPendingItemsUrl,
  getHabitaProcessStatusHistoryUrl,
  getHabitaProcessStatusUrl,
  getHabitaProcessValidationUrl,
} from './habitaApiUrls';

type ValidationResponseWithHttpStatus = HabitaSubmitValidationResponse & {
  httpStatus: number;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function cleanString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function finiteNumber(value: unknown): number | null {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function recordArray<T>(
  value: unknown,
  mapper: (record: Record<string, unknown>) => T | null
): T[] {
  return Array.isArray(value)
    ? value.map((item) => (isRecord(item) ? mapper(item) : null)).filter((item): item is T => item !== null)
    : [];
}

function collectErrorPayloadMessages(value: unknown): string[] {
  const directMessage = cleanString(value);
  if (directMessage) return [directMessage];

  if (Array.isArray(value)) {
    return value.flatMap(collectErrorPayloadMessages);
  }

  if (!isRecord(value)) return [];

  const messages = [
    cleanString(value.message),
    cleanString(value.msg),
    cleanString(value.error),
    cleanString(value.stage),
    cleanString(value.transition_error),
  ].filter((message): message is string => Boolean(message));

  const nestedMessages = Object.values(value)
    .filter((item) => isRecord(item) || Array.isArray(item))
    .flatMap(collectErrorPayloadMessages);

  return Array.from(new Set([...messages, ...nestedMessages]));
}

function apiErrorMessages(error: unknown): string[] {
  if (error instanceof HttpError) {
    return collectErrorPayloadMessages(error.data);
  }

  const message = error instanceof Error ? cleanString(error.message) : null;
  return message ? [message] : [];
}

function normalizeErrorText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

function isUserFacingApiMessage(value: string): boolean {
  const normalized = normalizeErrorText(value);
  if (normalized.includes('/webhook/') || normalized.includes('http error')) return false;
  if (/^[a-z0-9_:-]+$/.test(normalized)) return false;

  return true;
}

function friendlyValidationError(
  error: unknown,
  fallbackMessage = 'Não foi possível solicitar o ajuste documental agora.'
): Error {
  if (!(error instanceof HttpError) && error instanceof Error && error.message.trim()) {
    const localMessage = error.message.trim();
    if (
      localMessage.startsWith('A pasta precisa') ||
      localMessage.startsWith('Ciclo de validação') ||
      localMessage.startsWith('Não há ciclo') ||
      localMessage.startsWith('A API respondeu') ||
      localMessage.startsWith('Não foi possível abrir') ||
      localMessage.startsWith('Não foi possível validar') ||
      localMessage.startsWith('Selecione') ||
      localMessage.startsWith('Usuário responsável')
    ) {
      return new Error(localMessage);
    }
  }

  if (error instanceof HttpError && (error.status === 401 || error.status === 403)) {
    return new Error('Sua sessão expirou. Faça login novamente para continuar.');
  }

  const apiMessages = apiErrorMessages(error);
  const apiMessage = normalizeErrorText(apiMessages.join(' | '));

  if (apiMessage.includes('transition_failed') || apiMessage.includes('transicao invalida')) {
    return new Error('A pasta precisa estar em uma etapa que permita validação documental.');
  }

  if (
    apiMessage.includes('sem nenhum item rejected') ||
    (apiMessage.includes('request_adjustment') && apiMessage.includes('rejected') && apiMessage.includes('sem'))
  ) {
    return new Error('Selecione ao menos um item para solicitar ajuste.');
  }

  if (
    (apiMessage.includes('file') || apiMessage.includes('documento') || apiMessage.includes('process_file')) &&
    apiMessage.includes('nao encontrado')
  ) {
    return new Error('Um dos documentos selecionados não foi encontrado neste processo.');
  }

  if (
    (apiMessage.includes('validation') || apiMessage.includes('ciclo')) &&
    (apiMessage.includes('open') || apiMessage.includes('aberto')) &&
    (apiMessage.includes('nao') || apiMessage.includes('estado atual') || apiMessage.includes('fechado'))
  ) {
    return new Error('O ciclo de validação não está aberto. Recarregue a pasta e tente novamente.');
  }

  if (apiMessage.includes('validation') && apiMessage.includes('nao pertence')) {
    return new Error('O ciclo de validação não pertence a esta pasta. Recarregue a pasta e tente novamente.');
  }

  const readableApiMessage = apiMessages.find(isUserFacingApiMessage);
  if (readableApiMessage) {
    return new Error(readableApiMessage);
  }

  return new Error(fallbackMessage);
}

function normalizeCycle(value: unknown): HabitaValidationCycle | null {
  if (!isRecord(value)) return null;
  const id = finiteNumber(value.id);
  if (id === null) return null;

  return {
    id,
    cycle_number: finiteNumber(value.cycle_number),
    kind: cleanString(value.kind),
    state: cleanString(value.state),
    opened_at: cleanString(value.opened_at),
    opened_by: cleanString(value.opened_by),
    summary: cleanString(value.summary),
  };
}

function normalizeValidationDataItem(record: Record<string, unknown>): HabitaValidationDataItem | null {
  const attributeKey = cleanString(record.attribute_key);
  if (!attributeKey) return null;

  return {
    attribute_key: attributeKey,
    value: record.value,
    source: cleanString(record.source),
    confidence: finiteNumber(record.confidence),
    validation_state: cleanString(record.validation_state),
    validated_by: cleanString(record.validated_by),
    validated_at: cleanString(record.validated_at),
  };
}

function normalizeValidationFileItem(record: Record<string, unknown>): HabitaValidationFileItem | null {
  const processFileId = finiteNumber(record.process_file_id);
  if (processFileId === null) return null;

  return {
    process_file_id: processFileId,
    doc_type: cleanString(record.doc_type),
    doc_subtype: cleanString(record.doc_subtype),
    name: cleanString(record.name),
    status: cleanString(record.status),
    ai_status: cleanString(record.ai_status),
    rejection_source: cleanString(record.rejection_source),
    rejection_reason: cleanString(record.rejection_reason),
    validated_by: cleanString(record.validated_by),
    validated_at: cleanString(record.validated_at),
  };
}

function normalizeValidationState(value: unknown, processId: string | number): HabitaProcessValidationState {
  const record = isRecord(value) ? value : {};

  return {
    process_id: finiteNumber(record.process_id) ?? processId,
    process_status: cleanString(record.process_status),
    customer_id: finiteNumber(record.customer_id),
    has_open_cycle: Boolean(record.has_open_cycle),
    cycle: normalizeCycle(record.cycle),
    data_items: recordArray(record.data_items, normalizeValidationDataItem),
    file_items: recordArray(record.file_items, normalizeValidationFileItem),
  };
}

function normalizePendingDataItem(record: Record<string, unknown>): HabitaPendingDataItem | null {
  const attributeKey = cleanString(record.attribute_key);
  if (!attributeKey) return null;

  return {
    attribute_key: attributeKey,
    current_value: record.current_value,
    source: cleanString(record.source),
    rejected_by: cleanString(record.rejected_by),
    rejected_at: cleanString(record.rejected_at),
    reason: cleanString(record.reason),
  };
}

function normalizePendingFileItem(record: Record<string, unknown>): HabitaPendingFileItem | null {
  const processFileId = finiteNumber(record.process_file_id);
  if (processFileId === null) return null;

  return {
    process_file_id: processFileId,
    doc_type: cleanString(record.doc_type),
    doc_subtype: cleanString(record.doc_subtype),
    name: cleanString(record.name),
    rejection_source: cleanString(record.rejection_source),
    rejection_reason: cleanString(record.rejection_reason),
    rule_check_data: isRecord(record.rule_check_data) ? record.rule_check_data : null,
    rejected_at: cleanString(record.rejected_at),
  };
}

function normalizePendingItems(value: unknown, processId: string | number): HabitaPendingItems {
  const record = isRecord(value) ? value : {};

  return {
    process_id: finiteNumber(record.process_id) ?? processId,
    process_status: cleanString(record.process_status),
    customer_id: finiteNumber(record.customer_id),
    pending_data: recordArray(record.pending_data, normalizePendingDataItem),
    pending_files: recordArray(record.pending_files, normalizePendingFileItem),
    has_pending: Boolean(record.has_pending),
    counts: isRecord(record.counts)
      ? {
          data: finiteNumber(record.counts.data),
          files: finiteNumber(record.counts.files),
        }
      : null,
  };
}

function normalizeStatusHistoryEntry(record: Record<string, unknown>): HabitaProcessStatusHistoryEntry | null {
  const toStatus = cleanString(record.to_status);
  const changedAt = cleanString(record.changed_at);

  if (!toStatus && !changedAt) return null;

  return {
    from_status: cleanString(record.from_status),
    to_status: toStatus,
    changed_at: changedAt,
    changed_by: cleanString(record.changed_by),
    trigger_source: cleanString(record.trigger_source),
    motive: cleanString(record.motive),
  };
}

function normalizeStatusHistory(value: unknown, processId: string | number): HabitaProcessStatusHistory {
  const record = isRecord(value) ? value : {};

  return {
    process_id: finiteNumber(record.process_id) ?? processId,
    history: recordArray(record.history, normalizeStatusHistoryEntry),
    count: finiteNumber(record.count),
  };
}

function pendingItemsContainRequestedFiles(
  pendingItems: HabitaPendingItems,
  files: HabitaRequestDocumentAdjustmentInput['files']
): boolean {
  const pendingFileIds = new Set(
    pendingItems.pending_files.map((file) => file.process_file_id)
  );

  return files.every((file) => pendingFileIds.has(file.processFileId));
}

export class ProcessValidationRepository {
  private httpRepository = new HttpRepository();

  async fetchValidationState(processId: string | number): Promise<HabitaProcessValidationState> {
    const response = await this.httpRepository.get<unknown>(getHabitaProcessValidationUrl(processId));
    return normalizeValidationState(response.data, processId);
  }

  async fetchPendingItems(processId: string | number): Promise<HabitaPendingItems> {
    const response = await this.httpRepository.get<unknown>(getHabitaProcessPendingItemsUrl(processId));
    return normalizePendingItems(response.data, processId);
  }

  async fetchStatusHistory(processId: string | number): Promise<HabitaProcessStatusHistory> {
    const response = await this.httpRepository.get<unknown>(getHabitaProcessStatusHistoryUrl(processId));
    return normalizeStatusHistory(response.data, processId);
  }

  async submitValidation(
    processId: string | number,
    body: HabitaSubmitValidationRequest
  ): Promise<ValidationResponseWithHttpStatus> {
    const response = await this.httpRepository.post<HabitaSubmitValidationResponse>(
      getHabitaProcessValidationUrl(processId),
      body
    );
    return {
      ...(response.data ?? {}),
      httpStatus: response.status,
    };
  }

  async transitionStatus(
    processId: string | number,
    body: HabitaProcessStatusTransitionRequest
  ): Promise<HabitaProcessStatusTransitionResponse> {
    const response = await this.httpRepository.post<HabitaProcessStatusTransitionResponse>(
      getHabitaProcessStatusUrl(processId),
      body
    );
    return response.data ?? {};
  }

  async validateFolder(
    processId: string | number,
    input: HabitaValidateFolderInput
  ): Promise<HabitaValidateFolderResult> {
    try {
      const reviewedBy = input.reviewedBy.trim();

      if (!reviewedBy) {
        throw new Error('Usuário responsável pela revisão não identificado.');
      }

      const validationState = await this.fetchValidationState(processId);
      const validationId = validationState.has_open_cycle ? validationState.cycle?.id : null;

      if (!validationId) {
        throw new Error('Não há ciclo de validação aberto para esta pasta.');
      }

      const validation = await this.submitValidation(processId, {
        validation_id: validationId,
        reviewed_by: reviewedBy,
        outcome: 'APPROVE_FOLDER',
        data_items: validationState.data_items.map((item) => ({
          attribute_key: item.attribute_key,
          action: 'CONFIRMED',
        })),
        file_items: validationState.file_items.map((item) => ({
          process_file_id: item.process_file_id,
          action: 'CONFIRMED',
        })),
      });

      const retryStatus = validation.retry_hint?.to_status?.trim();
      const shouldRetryTransition = validation.httpStatus === 207 || validation.status === 'partial_success';
      let retryTransition: HabitaProcessStatusTransitionResponse | undefined;
      let retryTransitionError: string | undefined;

      if (shouldRetryTransition && retryStatus) {
        try {
          retryTransition = await this.transitionStatus(processId, {
            to_status: retryStatus,
            changed_by: reviewedBy,
            motive: 'Pasta validada e encaminhada para Pré-análise.',
          });
        } catch (retryError) {
          console.error('Erro ao concluir transição após validação parcial:', retryError);
          retryTransitionError = 'A validação foi registrada, mas a atualização de status precisa ser concluída.';
        }
      } else if (shouldRetryTransition) {
        retryTransitionError = 'A validação foi registrada, mas a API não retornou a transição de status para concluir.';
      }

      return {
        validation,
        retryTransition,
        retryTransitionError,
      };
    } catch (error) {
      throw friendlyValidationError(error, 'Não foi possível validar a pasta agora.');
    }
  }

  async requestDocumentAdjustment(
    processId: string | number,
    input: HabitaRequestDocumentAdjustmentInput
  ): Promise<HabitaRequestDocumentAdjustmentResult> {
    try {
      const reviewedBy = input.reviewedBy.trim();
      const files = input.files.filter((file) => (
        Number.isFinite(file.processFileId) && file.rejectionReason.trim()
      ));

      if (!reviewedBy) {
        throw new Error('Usuário responsável pela revisão não identificado.');
      }

      if (files.length === 0) {
        throw new Error('Selecione ao menos um documento para ajuste.');
      }

      const validationState = await this.fetchValidationState(processId);
      const validationId = validationState.has_open_cycle ? validationState.cycle?.id : null;

      if (!validationId) {
        throw new Error('Não é possivel solicitar ajuste na etapa atual.');
      }

      const validation = await this.submitValidation(processId, {
        validation_id: validationId,
        reviewed_by: reviewedBy,
        outcome: 'REQUEST_ADJUSTMENT',
        data_items: [],
        file_items: files.map((file) => ({
          process_file_id: file.processFileId,
          action: 'REJECTED',
          rejection_reason: file.rejectionReason.trim(),
        })),
      });

      const retryStatus = validation.retry_hint?.to_status?.trim();
      const shouldRetryTransition = validation.httpStatus === 207 || validation.status === 'partial_success';
      let retryTransitionError: string | undefined;

      if (shouldRetryTransition && retryStatus) {
        retryTransitionError = 'Ajuste registrado, mas a atualização de status precisa ser concluída.';
      } else if (shouldRetryTransition) {
        retryTransitionError = 'Ajuste registrado, mas a atualização de status precisa ser concluída.';
      }

      const pendingItems = await this.fetchPendingItems(processId);
      if (!pendingItemsContainRequestedFiles(pendingItems, files)) {
        throw new Error('A API respondeu, mas não retornou os documentos selecionados na lista de pendências.');
      }

      return {
        validation,
        retryTransitionError,
      };
    } catch (error) {
      throw friendlyValidationError(error);
    }
  }
}
